import React from 'react';
import { Link } from 'react-router-dom';
import { FaInfoCircle, FaExclamationTriangle, FaCheck, FaCrown } from 'react-icons/fa';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../hooks/useAuth';

/**
 * Component to display subscription status alerts to users
 * Shows appropriate alerts for expired, expiring soon, or limited tier access
 */
const SubscriptionAlert = () => {
  const { 
    subscription, 
    isSubscriptionActive, 
    getCurrentTier, 
    getDaysRemaining, 
    isExpiringSoon 
  } = useSubscription();
  const { user } = useAuth();
  
  // If loading or no subscription data yet, don't show anything
  if (!subscription) {
    return null;
  }
  
  // Current tier information
  const currentTier = getCurrentTier() || 'free';
  const tierNames = {
    'free': 'Free',
    'essential': 'Essential',
    'growth': 'Growth',
    'plus': 'Plus',
    'ignite': 'Ignite',
    'elevate': 'Elevate'
  };
  const displayTierName = tierNames[currentTier] || currentTier;
  
  // No alerts for premium tiers that are active and not expiring soon
  const isPremiumTier = user?.role === 'parent' 
    ? ['plus', 'growth', 'essential'].includes(getCurrentTier()) && getCurrentTier() !== 'free'
    : ['elevate'].includes(getCurrentTier());
    
  if (isSubscriptionActive() && !isExpiringSoon() && isPremiumTier) {
    // For active premium subscriptions, just show the current plan banner
    return (
      <div className="mb-6 p-4 border border-green-200 rounded-md bg-green-50 text-green-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FaCrown className="text-xl text-green-500 mr-3" />
            <div>
              <h3 className="font-semibold">Current Plan: {displayTierName}</h3>
              <p className="text-sm">Your subscription is active with {getDaysRemaining()} days remaining.</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium mb-1">
              {subscription.billingCycle === 'annual' ? 'Annual' : 'Quarterly'} Billing
            </div>
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center">
              <FaCheck className="mr-1" /> Active
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Determine alert type and content
  let alertType = 'info';
  let icon = <FaInfoCircle className="text-xl" />;
  let title = '';
  let message = '';
  let actionLink = '';
  let actionText = '';
  
  if (!isSubscriptionActive()) {
    // Subscription expired or inactive
    alertType = 'error';
    icon = <FaExclamationTriangle className="text-xl" />;
    title = 'Subscription Inactive';
    message = 'Your subscription has expired. Renew now to continue accessing premium features.';
    actionLink = user?.role === 'parent' ? '/parent/subscription' : '/subscriptions';
    actionText = 'Renew Subscription';
  } else if (isExpiringSoon()) {
    // Subscription expiring soon
    alertType = 'warning';
    icon = <FaExclamationTriangle className="text-xl" />;
    title = 'Subscription Expiring Soon';
    message = `Your ${displayTierName} plan will expire in ${getDaysRemaining()} days. Renew now to maintain uninterrupted access.`;
    actionLink = user?.role === 'parent' ? '/parent/subscription' : '/subscriptions';
    actionText = 'Renew Now';
  } else if (getCurrentTier() === 'free') {
    // Free tier alert
    alertType = 'info';
    icon = <FaCrown className="text-xl" />;
    title = 'Upgrade Available';
    
    if (user?.role === 'parent') {
      message = 'You\'re using the free tier with limited features. Upgrade to add children and access premium features.';
    } else {
      message = 'You\'re using the free tier. Upgrade to access premium features and enhanced capabilities.';
    }
    
    actionLink = user?.role === 'parent' ? '/parent/subscription' : '/subscriptions';
    actionText = 'View Plans';
  } else if (getCurrentTier() === 'essential') {
    // Essential tier - suggest upgrade
    alertType = 'info';
    icon = <FaCrown className="text-xl" />;
    title = 'Upgrade Available';
    message = 'You\'re on the Essential plan. Upgrade to Growth or Plus for additional features and higher usage limits.';
    actionLink = user?.role === 'parent' ? '/parent/subscription' : '/subscriptions';
    actionText = 'View Premium Plans';
  }
  
  // Alert color classes based on type
  const alertClasses = {
    info: 'bg-blue-50 text-blue-800 border-blue-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    success: 'bg-green-50 text-green-800 border-green-200'
  };
  
  const iconClasses = {
    info: 'text-blue-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
    success: 'text-green-500'
  };
  
  const buttonClasses = {
    info: 'bg-blue-500 hover:bg-blue-600',
    warning: 'bg-yellow-500 hover:bg-yellow-600',
    error: 'bg-red-500 hover:bg-red-600',
    success: 'bg-green-500 hover:bg-green-600'
  };
  
  // Add current subscription banner
  const subscriptionBanner = (
    <div className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FaCrown className="text-lg text-gray-500 mr-2" />
          <div>
            <h3 className="font-medium">Current Plan: {displayTierName}</h3>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center ${isSubscriptionActive() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {isSubscriptionActive() ? 'Active' : 'Inactive'}
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="mb-6">
      {subscriptionBanner}
      <div className={`p-4 border rounded-md ${alertClasses[alertType]}`}>
        <div className="flex items-start">
          <div className={`mr-3 mt-0.5 ${iconClasses[alertType]}`}>
            {icon}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold mb-1">{title}</h3>
            <p className="text-sm mb-3">{message}</p>
            {actionLink && (
              <Link 
                to={actionLink} 
                className={`inline-block px-4 py-2 text-sm text-white rounded ${buttonClasses[alertType]} transition-colors`}
              >
                {actionText}
              </Link>
            )}
          </div>
          
          {/* Remaining Days Badge - Only show if subscription is active */}
          {isSubscriptionActive() && (
            <div className="ml-4 flex flex-col items-center justify-center">
              {isExpiringSoon() ? (
                <>
                  <span className="text-sm font-bold">{getDaysRemaining()}</span>
                  <span className="text-xs">days left</span>
                </>
              ) : (
                <FaCheck className="text-green-500 text-xl" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAlert; 